import * as React from "react";
import {
  Image as ImageIcon,
  Minus,
  Plus,
  RefreshCw,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { Text } from "../../../ui/components/Text";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import { vars } from "../../../ui/styles/theme.css";
import type {
  ChangeKind,
  ChangeSet,
  ChangeSetItem,
  DataTransferEntity,
} from "../../lib/api/data-transfer.types";
import {
  getYoutubeThumbnailUrl,
  getYoutubeVideoId,
  isYoutubeUrl,
} from "../../lib/media/youtube";

export type ChangeRow = ChangeSetItem & {
  imageUrl?: string | null;
};

export const KIND_LABEL: Record<ChangeKind, string> = {
  added: "addition",
  updated: "update",
  deleted: "deletion",
  unchanged: "unchanged",
};

export const KIND_COLOR: Record<ChangeKind, string> = {
  added: "#2e7d32",
  updated: "#1565c0",
  deleted: "#c62828",
  unchanged: "#6b7280",
};

const SUMMARY_CHIPS: Array<{
  key: "added" | "updated" | "deleted" | "unchanged";
  singular: string;
  plural: string;
  color: string;
  bg: string;
  border: string;
  Icon: LucideIcon;
}> = [
  {
    key: "added",
    singular: "addition",
    plural: "additions",
    color: "#166534",
    bg: "#F0FDF4",
    border: "#BBF7D0",
    Icon: Plus,
  },
  {
    key: "updated",
    singular: "update",
    plural: "updates",
    color: "#1D4ED8",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    Icon: RefreshCw,
  },
  {
    key: "deleted",
    singular: "deletion",
    plural: "deletions",
    color: "#B91C1C",
    bg: "#FEF2F2",
    border: "#FECACA",
    Icon: Trash2,
  },
  {
    key: "unchanged",
    singular: "unchanged",
    plural: "unchanged",
    color: "#4B5563",
    bg: "#F3F4F6",
    border: "#E5E7EB",
    Icon: Minus,
  },
];

const thumbCellStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 6,
  overflow: "hidden",
  background: vars.color.muted,
  border: `1px solid ${vars.color.border}`,
  flexShrink: 0,
  position: "relative",
};

const thumbPlaceholderStyle: React.CSSProperties = {
  ...thumbCellStyle,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export function ChangeSummaryChips({
  summary,
}: {
  summary: {
    added: number;
    updated: number;
    deleted: number;
    unchanged?: number;
  };
}) {
  const chips = SUMMARY_CHIPS.filter((c) => (summary[c.key] ?? 0) > 0);
  if (!chips.length) {
    return (
      <Text level="body-small" color="muted" style={{ textAlign: "center" }}>
        No pending changes
      </Text>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 8,
      }}
    >
      {chips.map(({ key, singular, plural, color, bg, border, Icon }) => {
        const n = summary[key] ?? 0;
        return (
          <span
            key={key}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 9999,
              background: bg,
              border: `1px solid ${border}`,
              color,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.3,
              whiteSpace: "nowrap",
            }}
          >
            <Icon size={13} strokeWidth={2.25} />
            {n} {n === 1 ? singular : plural}
          </span>
        );
      })}
    </div>
  );
}

function resolveItemImageUrl(
  entity: DataTransferEntity,
  items: unknown[],
  change: ChangeSetItem,
): string | null {
  if (entity === "certification-modules") {
    const fromItems = (
      items as Array<{
        slug?: string;
        heroImageUrl?: string | null;
        trainings?: Array<{
          thumbnailUrl?: string | null;
          videoUrl?: string | null;
        }>;
      }>
    ).find((item) => item.slug === change.matchKey);

    const after = change.after as
      | {
          heroImageUrl?: string | null;
          trainings?: Array<{
            thumbnailUrl?: string | null;
            videoUrl?: string | null;
          }>;
        }
      | undefined;

    const before = change.before as
      | {
          heroImageUrl?: string | null;
          trainings?: Array<{
            thumbnailUrl?: string | null;
            videoUrl?: string | null;
          }>;
        }
      | undefined;

    const mod = fromItems ?? after ?? before;
    if (!mod) return null;
    if (mod.heroImageUrl) return mod.heroImageUrl;
    for (const t of mod.trainings ?? []) {
      if (t.thumbnailUrl) return t.thumbnailUrl;
      if (t.videoUrl && isYoutubeUrl(t.videoUrl)) {
        const ytId = getYoutubeVideoId(t.videoUrl);
        if (ytId) return getYoutubeThumbnailUrl(ytId);
      }
    }
    return null;
  }

  if (entity === "organizations") {
    const fromItems = (items as Array<{ slug?: string; logoUrl?: string | null }>).find(
      (item) => item.slug === change.matchKey,
    );
    const after = change.after as { logoUrl?: string | null } | undefined;
    const before = change.before as { logoUrl?: string | null } | undefined;
    return fromItems?.logoUrl ?? after?.logoUrl ?? before?.logoUrl ?? null;
  }

  if (entity === "languages") {
    const fromItems = (
      items as Array<{
        code?: string;
        flagUrl?: string | null;
        flagMediaKey?: string | null;
      }>
    ).find((item) => item.code === change.matchKey);
    const after = change.after as {
      flagUrl?: string | null;
      flagMediaKey?: string | null;
    } | undefined;
    const before = change.before as {
      flagUrl?: string | null;
      flagMediaKey?: string | null;
    } | undefined;
    return (
      fromItems?.flagUrl ??
      after?.flagUrl ??
      before?.flagUrl ??
      null
    );
  }

  return null;
}

export function enrichChangeRows(
  entity: DataTransferEntity,
  items: unknown[],
  changeset: ChangeSet,
): ChangeRow[] {
  return changeset.items.map((item) => ({
    ...item,
    imageUrl: resolveItemImageUrl(entity, items, item),
  }));
}

export const changeSetColumns: ColumnDefinition<ChangeRow>[] = [
  {
    id: "image",
    label: "",
    width: 56,
    enableHiding: false,
    pinnedLeft: true,
    render: (row) =>
      row.imageUrl ? (
        <div style={thumbCellStyle}>
          <img
            src={row.imageUrl}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </div>
      ) : (
        <div style={thumbPlaceholderStyle}>
          <ImageIcon size={16} style={{ opacity: 0.4, color: vars.color.fgMuted }} />
        </div>
      ),
  },
  {
    id: "kind",
    label: "Type",
    sortable: true,
    render: (row) => (
      <span style={{ color: KIND_COLOR[row.kind], fontWeight: 600 }}>
        {KIND_LABEL[row.kind]}
      </span>
    ),
  },
  {
    id: "label",
    label: "Item",
    sortable: true,
    render: (row) => row.label,
  },
  {
    id: "matchKey",
    label: "Key",
    render: (row) => row.matchKey,
  },
  {
    id: "fields",
    label: "Changes",
    render: (row) => {
      const fields = row.fields ?? [];
      if (!fields.length) return "—";
      return (
        fields
          .slice(0, 3)
          .map((f) => f.field)
          .join(", ") + (fields.length > 3 ? ` +${fields.length - 3}` : "")
      );
    },
  },
];
